import { last } from 'lodash';

import type { ClaimStageConfiguration } from '~/components/ClaimStages/types';
import type { ClaimConductorStageHistoryResponseClaimStageHistoryEntry as StageHistoryEntry } from '~/server_shared/openapi/data-contracts';

import { stageMoveDirection } from '../stageDirection';

import type { StageStep } from './types';

/**
 * Every enabled stage the conductor is configured for once, in the configured order, marked by
 * where it sits relative to the claim's current stage. The current stage is kept even when the
 * conductor is not configured for it, so the bar always says where the claim is. Empty when
 * the current stage is not an enabled one — there is nothing to mark.
 *
 * ponytail: position only — a stage behind the claim reads as done even if the claim skipped it
 * or was sent back from one ahead. The bar reads the claim's stage history for one thing only: the
 * current stage, when the claim was sent back to it (`stageSentBackTo`).
 */
export const buildStageSteps = (
  stages: ClaimStageConfiguration[],
  conductorStageKeys: string[],
  currentStageKey: string | undefined
): StageStep[] => {
  const flow = stages.filter(
    (stage) => stage.is_enabled && (stage.key === currentStageKey || conductorStageKeys.includes(stage.key))
  );
  const currentIndex = flow.findIndex((stage) => stage.key === currentStageKey);
  if (currentIndex === -1) return [];

  return flow.map(({ key, label }, index) => ({
    key,
    label,
    state: index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'ahead',
  }));
};

/**
 * The stage the claim's last move sent it back to, read against `flow` with the helper the log's stages rail
 * reads every move with, so the bar and the rail agree on which moves went back. `undefined` when the last
 * move did not go back, or there is none.
 */
export const stageSentBackTo = (history: StageHistoryEntry[], flow: string[]): string | undefined => {
  const lastMove = last(history);

  return lastMove && stageMoveDirection(flow, lastMove.previous_stage_key, lastMove.new_stage_key) === 'back'
    ? lastMove.new_stage_key
    : undefined;
};

/** How long the claim takes to cross one connector when it moves forward. */
export const STEP_TRANSITION_MS = 400;

/**
 * When a step's connector and marker start to change after the claim moved from `fromIndex` to
 * `toIndex`: one connector at a time from the stage it left, each marker as the line reaches it, so
 * a jump over stages reads as the claim travelling there rather than the bar repainting. Nothing
 * waits when the claim moved back or has not moved.
 */
export const stepDelays = (
  index: number,
  fromIndex: number | undefined,
  toIndex: number
): { connector: number; marker: number } => {
  if (fromIndex === undefined || toIndex <= fromIndex || index <= fromIndex) return { connector: 0, marker: 0 };

  return {
    connector: (index - fromIndex - 1) * STEP_TRANSITION_MS,
    marker: (index - fromIndex) * STEP_TRANSITION_MS,
  };
};
